<template>
  <UPopover
    v-model:open="isOpen"
    :content="filterContent"
    :ui="filterUi"
  >
    <button
      type="button"
      class="-ml-px border cursor-pointer flex focus:outline-none focus:z-(--z-index-ds-lifted) items-center px-4 py-2 relative rounded-r-lg space-x-2 transition-colors"
      :class="[
        hasEventDetailsSelected
          ? 'z-(--z-index-ds-raised) border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 dark:border-primary/40 dark:bg-primary/20 dark:hover:bg-primary/25'
          : 'border-border-level-200 bg-surface-canvas text-content-level-700 hover:bg-surface-level-50 dark:border-border-inverse/10 dark:bg-surface-clear dark:text-content-level-300 dark:hover:bg-surface-canvas/5'
      ]"
    >
      <UsersIcon class="shrink-0 size-4" />

      <span class="font-medium text-sm">{{
        currentCount.provided
          ? $t(
              'commerce.eventDetailsFilter.guestsCount',
              { count: currentCount.value },
              currentCount.value
            )
          : $t('commerce.eventDetailsFilter.partySize')
      }}</span>

      <ChevronDownIcon class="shrink-0 size-4" />
    </button>

    <template #content>
      <div class="p-6">
        <!-- Number of Attendees Section -->
        <div class="mb-6">
          <h3 class="dark:text-content-inverse font-semibold mb-2 text-content-level-900 text-lg">
            {{ $t('commerce.eventDetailsFilter.numberOfGuests') }}
          </h3>

          <div class="flex items-center space-x-3">
            <button
              type="button"
              class="bg-surface-level-100 border border-border-level-300 dark:bg-surface-level-700 dark:border-border-level-600 dark:hover:bg-surface-level-600 dark:text-content-level-400 flex h-10 hover:bg-surface-level-200 items-center justify-center rounded-lg text-content-level-600 transition-colors w-10"
              @click="decreaseGuestCount"
            >
              <span class="font-medium text-lg">-</span>
            </button>

            <input
              v-model="guestCountDraft.input.value"
              type="text"
              inputmode="numeric"
              :aria-label="$t('commerce.eventDetailsFilter.numberOfGuests')"
              :aria-invalid="isGuestCountRejected"
              :placeholder="$t('commerce.eventDetailsFilter.guestCountPlaceholder')"
              class="bg-surface-canvas border border-border-level-300 dark:bg-surface-level-700 dark:border-border-level-600 dark:text-content-inverse flex-1 focus:outline-none focus:ring-2 focus:ring-primary px-3 py-2 rounded-lg text-center text-content-level-900"
            />

            <button
              type="button"
              class="bg-surface-level-100 border border-border-level-300 dark:bg-surface-level-700 dark:border-border-level-600 dark:hover:bg-surface-level-600 dark:text-content-level-400 flex h-10 hover:bg-surface-level-200 items-center justify-center rounded-lg text-content-level-600 transition-colors w-10"
              @click="increaseGuestCount"
            >
              <span class="font-medium text-lg">+</span>
            </button>
          </div>

          <p
            v-if="isGuestCountRejected"
            class="dark:text-danger-400 mt-2 text-danger-600 text-sm"
            role="alert"
          >
            {{ $t('commerce.eventDetailsFilter.guestCountInvalid') }}
          </p>

          <p class="dark:text-content-level-400 mt-2 text-content-level-600 text-xs">
            {{ $t('commerce.eventDetailsFilter.guestCountHint') }}
          </p>
        </div>

        <!-- Event Name Section -->
        <!--
          <div class="mb-6">
          <h3 class="text-lg font-semibold text-content-level-900 dark:text-content-inverse mb-2">
          Event name (optional)
          </h3>
          <input
          v-model="eventName"
          type="text"
          placeholder="Enter event name"
          class="w-full px-3 py-2 text-sm border border-border-level-300 dark:border-border-level-600 rounded-lg bg-surface-canvas dark:bg-surface-level-700 text-content-level-900 dark:text-content-inverse focus:outline-none focus:ring-2 focus:ring-primary"
          />
          </div>
        -->

        <!-- Action Buttons -->
        <div class="flex justify-end space-x-3">
          <button
            v-if="hasEventName"
            type="button"
            class="bg-surface-canvas border border-border-level-300 cursor-pointer dark:bg-surface-clear dark:border-border-inverse/20 dark:hover:bg-surface-canvas/10 dark:text-content-level-200 font-medium hover:bg-surface-level-50 px-4 py-2 rounded-md text-content-level-700 text-sm"
            @click="handleClearClick"
          >
            {{ $t('commerce.eventDetailsFilter.clear') }}
          </button>

          <button
            type="button"
            :disabled="isGuestCountRejected"
            class="bg-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 font-medium hover:bg-primary/90 px-6 py-2 rounded-md text-content-inverse text-sm"
            @click="handleEventDetailsUpdate"
          >
            {{ $t('commerce.eventDetailsFilter.update') }}
          </button>
        </div>
      </div>
    </template>
  </UPopover>
</template>

<script setup lang="ts">
import UPopover from '@nuxt/ui/components/Popover.vue'
import { UsersIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { useGuestCountDraft } from '~/composables/useGuestCountDraft'
import { normalizeEventName } from '~/utils/normalizeEventName'
import type { GuestCountState } from '~/utils/GuestCountState'
import { readGuestCount } from '~/utils/readGuestCount'
import { overlayContentClasses } from '~/utils/overlayContentClasses'

// Props
const props = withDefaults(
  defineProps<{
    /** Current guest count used by the filter. */
    currentGuestCount?: string | number | null
    /** Current event name used by the filter. */
    currentEventName?: string | null
  }>(),
  { currentEventName: null, currentGuestCount: null }
)

// Emits
const emit = defineEmits<{
  'update:eventDetails': [value: EventDetails]
}>()

interface EventDetails {
  guestCount: GuestCountState
  eventName: string | null
}

// A party size is optional: Update saves an empty one and stays disabled only while the typed
// draft is not a valid party size.
const guestCountDraft = useGuestCountDraft()
const eventName = ref('')
const isGuestCountRejected = computed(
  () => guestCountDraft.input.value.trim() !== '' && !guestCountDraft.count.value.provided
)

// Computed properties
const currentCount = computed(() => readGuestCount(props.currentGuestCount))
const hasEventName = computed(() => (props.currentEventName?.length ?? 0) > 0)
const hasEventDetailsSelected = computed(() => currentCount.value.provided || hasEventName.value)

// Methods
const stepGuestCount = (step: number): void => {
  const current = guestCountDraft.count.value
  const stepped = readGuestCount((current.provided ? current.value : 0) + step)
  if (stepped.provided) guestCountDraft.input.value = String(stepped.value)
}

const decreaseGuestCount = () => {
  stepGuestCount(-1)
}

const increaseGuestCount = () => {
  stepGuestCount(1)
}

const handleEventDetailsUpdate = () => {
  const count = guestCountDraft.attempt()
  if (isGuestCountRejected.value) return

  emit('update:eventDetails', {
    eventName: normalizeEventName(eventName.value),
    guestCount: count
  })

  isOpen.value = false
}

const isOpen = ref(false)

const filterContent = { align: 'center', sideOffset: 12 } as const

const filterUi = {
  content: [
    overlayContentClasses.frame,
    overlayContentClasses.panel,
    'max-w-md overflow-hidden text-sm/6 w-screen'
  ].join(' ')
} as const

// Allow parent to open the popover programmatically (e.g. from cart drawer)
const open = () => {
  isOpen.value = true
}

defineExpose({ open })

// Clear resets the event name only and keeps the party size; emptying the field and pressing
// Update is how a party size is removed.
const handleClearClick = () => {
  eventName.value = ''

  const count = currentCount.value
  if (count.provided) {
    emit('update:eventDetails', {
      eventName: null,
      guestCount: count
    })
  }

  isOpen.value = false
}

// Initialize from props
const initializeFromProps = () => {
  guestCountDraft.reset(props.currentGuestCount)

  if (hasEventName.value) {
    eventName.value = props.currentEventName ?? ''
  }
}

// Watch for prop changes to update selections
watch(
  () => [props.currentGuestCount, props.currentEventName],
  () => {
    initializeFromProps()
  },
  { immediate: true }
)
</script>
